import React from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { Search } from "lucide-react";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";

interface PageHeaderProps {
  /** White part of the title, e.g. "Market". */
  title: string;
  /** Green part of the title, e.g. "Alerts". */
  accent?: string;
  subtitle?: string;
  /** Hide the logo row (pages that sit under another header). */
  hideTopBar?: boolean;
  actions?: React.ReactNode;
}

function initials(first?: string, last?: string, username?: string): string {
  const name = [first, last].filter(Boolean).join(" ") || username || "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

/** Mobile page header from the redesign: logo row, two-tone title, subtitle. */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  accent,
  subtitle,
  hideTopBar = false,
  actions,
}) => {
  const navigate = useNavigate();
  const user = useLaunchParams().initData?.user;

  return (
    <div className="flex flex-col gap-6">
      {!hideTopBar && (
        <div className="flex items-center gap-3">
          <div className="flex flex-1 items-center gap-1">
            <img src="/logo-mark.svg" alt="" width={38} height={32} />
            <img src="/logo-wordmark.svg" alt="Trigger" width={84} height={24} />
          </div>
          <button
            type="button"
            aria-label="Analyze a token"
            onClick={() => navigate("/analyze")}
            className="glass flex items-center justify-center !rounded-lg p-2"
          >
            <Search className="size-4 text-[#d4d4d4]" />
          </button>
          <NotificationsBell size="sm" />
          <button
            type="button"
            aria-label="Settings"
            onClick={() => navigate("/settings")}
            className="flex size-8 items-center justify-center overflow-hidden rounded-full border border-[#2dcc89] bg-[#262626] text-sm font-semibold text-[#a3a3a3]"
          >
            {user?.photoUrl ? (
              <img src={user.photoUrl} alt="" className="size-full object-cover" />
            ) : (
              initials(user?.firstName, user?.lastName, user?.username)
            )}
          </button>
        </div>
      )}
      {(title || subtitle || actions) && (
      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <h1 className="text-2xl font-semibold leading-8 text-white">
            {title}
            {accent && <span className="text-accent"> {accent}</span>}
          </h1>
          {subtitle && (
            <p className="text-base leading-6 text-[#a3a3a3]">{subtitle}</p>
          )}
        </div>
        {actions}
      </div>
      )}
    </div>
  );
};
